import { CSSProperties, useMemo } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { FloatingScrollButtons } from '../components/floating-scroll-buttons';
import { CALENDAR_Z_INDICES, MIN_COLUMN_WIDTH, SPACER_ROW_HEIGHT } from '../constants';
import { useScrollContainerRef } from '../hooks/use-scroll-container-ref';
import { useCalendarGridStore } from '../store/calendar-grid.store';
import { useZoomStore } from '../store/zoom.store';
import { AllDayCalendarEvent, CalendarGridCreateEvent, CalendarNavigationConfig, EmptyStateRenderProp } from '../types';
import { useScrollPreservation } from '../zoom/use-scroll-preservation';
import { ZoomControls } from '../zoom/zoom-controls';
import { AllDayEventsBar } from './all-day-events-bar';
import { BaseTimeZoneChipCell } from './base-timezone-chip-cell';
import { CalendarGridInteractionSurface } from './calendar-grid-interaction-surface';
import { CalendarGridProvider, CalendarGridProviderProps } from './calendar-grid-provider';
import { ColumnGroupHeaders, ColumnGroupBodies } from './column-group-sections';
import { DragCreateSelectionOverlay } from './drag-create-selection-overlay';
import { useDragCreateSelectionOverlay } from './drag-create-selection-overlay/use-drag-create-selection-overlay';
import { EmptyStateOverlays } from './empty-state-overlays';
import { EventDragDropContext } from './event-drag-drop-context';
import { TimeColumn } from './time-column';
import { TopCornerCells } from './top-corner-cells';

interface CalendarGridComponentProps {
  renderEmptyStateComponent?: EmptyStateRenderProp;
  navigationConfig?: CalendarNavigationConfig;
  onEventCreate?: (selection: CalendarGridCreateEvent) => void;
  onAllDayEventClick?: (event: AllDayCalendarEvent) => void;
  wrapperRef?: React.RefObject<HTMLElement>;
}

const CalendarGridComponent = ({
  renderEmptyStateComponent,
  navigationConfig,
  onEventCreate,
  onAllDayEventClick,
  wrapperRef,
}: CalendarGridComponentProps) => {
  const { containerRef, handleScroll, visibleHeight, visibleWidth } = useScrollContainerRef();
  useScrollPreservation(containerRef);

  const { groupIds, groupsById, columnBodyVariant, isColumnReorderDragging } = useCalendarGridStore([
    'groupIds',
    'groupsById',
    'columnBodyVariant',
    'isColumnReorderDragging',
  ]);
  const { pxPerMinute, resolvedRowHeightPx } = useZoomStore(['pxPerMinute', 'resolvedRowHeightPx']);

  const isGridBody = columnBodyVariant === 'grid';
  const isAllowDragCreateSelection = isGridBody && !!onEventCreate;

  const {
    startDragCreateSelection,
    progressDragCreateSelection,
    endDragCreateSelection,
    isDragCreateSelectionStarted,
  } = useDragCreateSelectionOverlay({
    isAllowDragCreateSelection,
    onEventCreate,
  });

  const gridTemplateColumns = useMemo(() => {
    return groupIds.reduce((acc, groupId) => {
      let columnCount = groupsById[groupId]?.columnIds.length ?? 0;
      // Force minimum 1 column so we have space for empty state overlay
      if (columnCount === 0) columnCount = 1;
      return `${acc} repeat(${columnCount}, minmax(var(--column-min-width), 1fr)) var(--group-separator-width)`;
    }, 'var(--time-column-width) ');
  }, [groupsById, groupIds]);

  const hasNoColumns = useMemo(
    () => groupIds.every((groupId) => groupsById[groupId]?.emptyStateVariant === 'no-columns'),
    [groupsById, groupIds]
  );
  const gridStyle = {
    gridTemplateColumns,
    '--row-height': `${pxPerMinute}px`,
    // for browser zoom case, --row-height changed to fractional value internally so we need to use that actual value to correctly place things in grid
    '--resolved-row-height': `${resolvedRowHeightPx}px`,
    '--viewport-height': `${visibleHeight}px`,
    '--viewport-width': `${visibleWidth}px`,
    '--column-min-width': `${MIN_COLUMN_WIDTH[columnBodyVariant]}px`,
    '--time-column-width': isGridBody ? '60px' : '40px',
    '--grid-body-spacer-size': isGridBody ? `${SPACER_ROW_HEIGHT}px` : '0px',
    ...(hasNoColumns ? { overflowY: 'hidden' } : {}),
    // rows: header, all-day bar (its own max-content row so it can expand/collapse), spacer, then body
    gridTemplateRows:
      columnBodyVariant === 'stacked'
        ? `var(--header-height) max-content var(--grid-body-spacer-size) auto`
        : `var(--header-height) max-content var(--grid-body-spacer-size) repeat(var(--total-mins), var(--row-height)) var(--grid-body-spacer-size)`,
    backgroundColor: theme.colors.neutral5,
    ...(isColumnReorderDragging ? { scrollSnapType: 'none', scrollBehavior: 'auto' } : {}),
  } as CSSProperties;

  const shouldShowTimeColumn = !hasNoColumns;
  const shouldShowBaseTimeZoneChipCell = isGridBody && groupIds.length === 1 && !hasNoColumns;
  const shouldShowEmptyStateOverlay = !!renderEmptyStateComponent;
  const shouldShowZoomControls = !hasNoColumns;
  const shouldShowHeaderRegion = !hasNoColumns;
  const shouldShowScrollButtons = !hasNoColumns;

  return (
    <CalendarGridInteractionSurface
      containerRef={containerRef}
      wrapperRef={wrapperRef}
      style={gridStyle}
      isGridBody={isGridBody}
      isAllowDragCreateSelection={isAllowDragCreateSelection}
      isDragCreateSelectionStarted={isDragCreateSelectionStarted}
      startDragCreateSelection={startDragCreateSelection}
      progressDragCreateSelection={progressDragCreateSelection}
      endDragCreateSelection={endDragCreateSelection}
    >
      <div css={headerRegionStyle}>
        {shouldShowScrollButtons && <TopCornerCells onScroll={handleScroll} navigationConfig={navigationConfig} />}
        {shouldShowBaseTimeZoneChipCell && <BaseTimeZoneChipCell />}
        {shouldShowHeaderRegion && <ColumnGroupHeaders />}
        <AllDayEventsBar onAllDayEventClick={onAllDayEventClick} />
      </div>
      {shouldShowTimeColumn && <TimeColumn />}
      <EventDragDropContext>
        <ColumnGroupBodies />
      </EventDragDropContext>
      <DragCreateSelectionOverlay isEnabled={isAllowDragCreateSelection} />
      {shouldShowEmptyStateOverlay && <EmptyStateOverlays renderEmptyStateComponent={renderEmptyStateComponent} />}
      {shouldShowZoomControls && <ZoomControls />}

      {shouldShowScrollButtons && isGridBody && (
        <FloatingScrollButtons onScroll={handleScroll} navigationConfig={navigationConfig} />
      )}
      {/* // Hiding this for now to avoid confusion in testing, but we can bring it back if we want to use it for debugging */}
      {/* <DebugControls /> */}
    </CalendarGridInteractionSurface>
  );
};

export const CalendarGrid = ({
  renderEmptyStateComponent,
  navigationConfig,
  onEventCreate,
  onAllDayEventClick,
  wrapperRef,
  ...props
}: CalendarGridProviderProps & CalendarGridComponentProps) => {
  return (
    <CalendarGridProvider {...props}>
      <CalendarGridComponent
        renderEmptyStateComponent={renderEmptyStateComponent}
        navigationConfig={navigationConfig}
        onEventCreate={onEventCreate}
        onAllDayEventClick={onAllDayEventClick}
        wrapperRef={wrapperRef}
      />
    </CalendarGridProvider>
  );
};

// Subgrids both axes from the outer grid so the header, all-day, and spacer rows scroll/stick as one unit
const headerRegionStyle = css({
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridTemplateRows: 'subgrid',
  gridColumn: '1 / -1',
  gridRow: '1 / 4',
  position: 'sticky',
  top: 0,
  zIndex: CALENDAR_Z_INDICES.groupHeader,
});
